import React, { memo, useEffect, useMemo, useState } from "react";

import {
  Alert,
  Button,
  Image,
  Input,
  message,
  Modal,
  Space,
  Tooltip,
  Typography,
} from "antd";
import PropTypes from "prop-types";
import { MdDownload } from "react-icons/md";

import Loader from "../../../Components/Loader/Loader";
import { usePrivateDocuments } from "../../../hooks";
import { showAppError } from "../../../Utils/error";
import { KYCVerificationStatus } from "../../../Utils/types";
import UserAPI from "../../api/UserAPI";

const IMAGE_MAX_SIZE = 240;

const uploadWrapperInnerStyle = {
  width: IMAGE_MAX_SIZE,
  height: IMAGE_MAX_SIZE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 8,
  backgroundColor: "var(--background)",
  overflow: "hidden",
  position: "relative",
};

/**
 * Reviews a creator's LUT (Letter of Undertaking) for FIRC export-earnings
 * compliance (TMG-2249). This is a manual verification (same process as GST
 * details) that is independent of the main KYC lifecycle. Verifying enables
 * FIRC for the creator permanently.
 */
const ReviewLUTModal = ({ kyc, isModalOpen, onClose, reloadList }) => {
  const [rejectionReason, setRejectionReason] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const isUnderReview =
    kyc?.lutVerificationStatus === KYCVerificationStatus.UNDER_REVIEW;

  const statusToSave = useMemo(
    () =>
      rejectionReason?.trim()
        ? KYCVerificationStatus.REJECTED
        : KYCVerificationStatus.VERIFIED,
    [rejectionReason]
  );

  const { isLoading, files, previewUrls } = usePrivateDocuments(
    useMemo(
      () => ({
        userId: kyc?._id,
        documentIds: [kyc?.lut?.frontImage || ""].filter(Boolean),
        hideToast: true,
      }),
      [kyc?._id, kyc?.lut?.frontImage]
    )
  );

  useEffect(() => {
    if (!isModalOpen) {
      setRejectionReason("");
      setIsSaving(false);
    }
  }, [isModalOpen]);

  // preload the existing rejection reason when re-opening a rejected LUT
  useEffect(() => {
    setRejectionReason(kyc?.rejectionReasons?.lut?.[0] || "");
  }, [kyc?.rejectionReasons?.lut]);

  const submitReview = async () => {
    setIsSaving(true);

    try {
      const resp = await UserAPI.verifyCreatorLUT(kyc?._id, {
        status: statusToSave,
        rejectionReason,
      });

      if (resp?.code === 200) {
        message.success(
          `LUT has been ${
            statusToSave === KYCVerificationStatus.VERIFIED
              ? "verified and FIRC compliance enabled"
              : "rejected"
          } successfully`
        );
        reloadList(statusToSave);
        onClose();
      } else {
        showAppError(resp);
      }
    } catch (error) {
      showAppError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const previewUrl = previewUrls?.[0];
  const file = files?.[0];

  const renderDocument = () => {
    if (isLoading) {
      return <Loader type="spinner" />;
    }

    if (!previewUrl) {
      return "No document uploaded";
    }

    return (
      <>
        <Image
          src={previewUrl}
          alt="LUT document"
          style={{
            width: "100%",
            height: "100%",
            maxWidth: IMAGE_MAX_SIZE,
            maxHeight: IMAGE_MAX_SIZE,
            objectFit: "contain",
          }}
        />
        {file ? (
          <Tooltip title="Open in new tab" placement="top">
            <Button
              shape="circle"
              icon={<MdDownload size={18} />}
              style={{ position: "absolute", top: 8, right: 8 }}
              onClick={() => window.open(URL.createObjectURL(file), "_blank")}
            />
          </Tooltip>
        ) : null}
      </>
    );
  };

  const renderStatusInfo = () => {
    if (isUnderReview) {
      return (
        <>
          <Alert
            type="info"
            showIcon
            message="Verifying the LUT enables FIRC export-earnings compliance for this creator permanently. This cannot be reverted."
          />
          <div>
            <Typography.Text type="secondary">
              Rejection reason (leave empty to verify)
            </Typography.Text>
            <Input.TextArea
              placeholder="Enter a rejection reason to reject the LUT"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 4 }}
              status={rejectionReason?.trim() ? "error" : ""}
              style={{ marginTop: 8 }}
            />
          </div>
        </>
      );
    }

    if (
      kyc?.lutVerificationStatus === KYCVerificationStatus.REJECTED &&
      kyc?.rejectionReasons?.lut?.length
    ) {
      return (
        <Alert
          type="error"
          showIcon
          message="Rejection reason"
          description={kyc.rejectionReasons.lut.join(", ")}
        />
      );
    }

    return null;
  };

  return (
    <Modal
      title={
        <Typography.Title level={5} style={{ marginBottom: 0 }}>
          {isUnderReview
            ? "Review LUT (FIRC Compliance)"
            : "View LUT (FIRC Compliance)"}
        </Typography.Title>
      }
      centered
      footer={
        isUnderReview ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              gap: 4,
            }}
          >
            <Button className="siteBtn grayBtn" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className={
                statusToSave === KYCVerificationStatus.VERIFIED
                  ? "siteBtn accentBtnV1 successBtn"
                  : "siteBtn accentBtnV1 danger"
              }
              loading={isSaving}
              onClick={submitReview}
              disabled={isSaving}
            >
              {statusToSave === KYCVerificationStatus.VERIFIED
                ? "Verify & Enable FIRC"
                : "Reject"}
            </Button>
          </div>
        ) : (
          <Button className="siteBtn grayBtn" onClick={onClose}>
            Close
          </Button>
        )
      }
      open={isModalOpen}
      onCancel={onClose}
      className="createPostModalv2 addSubscriberModal review-kyc-modal"
      wrapClassName="createPostModalDialogv2"
      maskClosable={false}
      width={640}
      destroyOnClose
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <div>
          <Typography.Text type="secondary">Creator</Typography.Text>
          <Typography.Title level={5} style={{ marginTop: 4, marginBottom: 0 }}>
            {kyc?.name} {kyc?.host ? `(${kyc.host})` : ""}
          </Typography.Title>
        </div>

        <div>
          <Typography.Text type="secondary">LUT Number</Typography.Text>
          <Typography.Title level={5} style={{ marginTop: 4, marginBottom: 0 }}>
            {kyc?.lut?.number || "N/A"}
          </Typography.Title>
        </div>

        <div>
          <Typography.Text type="secondary">LUT Document</Typography.Text>
          <div style={{ ...uploadWrapperInnerStyle, marginTop: 8 }}>
            {renderDocument()}
          </div>
        </div>

        {renderStatusInfo()}
      </Space>
    </Modal>
  );
};

ReviewLUTModal.propTypes = {
  kyc: PropTypes.shape().isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  reloadList: PropTypes.func.isRequired,
};

export default memo(ReviewLUTModal);
